<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">

      <!-- Login v1 -->
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <ok-logo />
          <h2 class="brand-text text-primary ml-1" />
        </b-link>
        <error-alert
          v-if="$route.hash === '#redirected'"
          :error="$t('auth.please-register-to-continue')"
          variant="warning"
        />
        <error-alert
          v-if="error"
          :error="error"
        />
        <b-card-title class="mb-1">
          {{ $t('auth.welcome-back') }} 👋
        </b-card-title>
        <b-card-text class="mb-2">
          {{ $t('auth.login-hint') }}
        </b-card-text>

        <!-- form -->
        <validation-observer
          ref="loginForm"
          #default="{invalid}"
        >
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent="submit"
          >
            <ok-text-input
              v-model="userEmail"
              dir="ltr"
              input-class="text-left"
              :label="$t('fields.email')"
              :is-required="true"
              hide-required-asterisk
            />
            <!-- password -->
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label class="auth-label">{{ $t('fields.password') }}</label>
                <b-link :to="{name:'auth:forget'}">
                  <small>{{ $t('auth.forgot-pass') }}</small>
                </b-link>
              </div>
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <password-input
                  v-model="password"
                  :label="$t('fields.password')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- checkbox -->
            <b-form-group>
              <b-form-checkbox
                id="remember-me"
                v-model="remember"
                name="checkbox-1"
              >
                {{ $t('auth.remember-me') }}
              </b-form-checkbox>
            </b-form-group>

            <!-- submit button -->
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="invalid || submitting"
            >
              {{ $t('auth.signin') }}
              <b-spinner
                v-if="submitting"
                small
                class="mr-50"
              />
            </b-button>
          </b-form>
        </validation-observer>

        <b-card-text class="text-center mt-2">
          <span>{{ $t('auth.no-account') }} </span>
          <b-link :to="{name:'register', query: $route.query}">
            <span>{{ $t('auth.create-account') }}</span>
          </b-link>
        </b-card-text>

        <div class="divider my-2">
          <div class="divider-text">
            {{ $t('general.or') }}
          </div>
        </div>

        <!-- social button -->
        <social-auth
          v-if="socialAuth"
          @error="error=$event"
          @clear-error="error=null"
        />
      </b-card>
      <!-- /Login v1 -->
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink,
  BFormGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BForm,
  BButton,
  BCard,
  BSpinner,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import OkTextInput from '@/@okmenu/components/inputs/OkTextInput'
import PasswordInput from '@/@okmenu/components/inputs/PasswordInput'
import OkLogo from '@/@okmenu/icons/OkLogo'
import SocialAuth from '@/views/auth/social-auth'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import StorageService from '@/services/storage-service'

export default {
  components: {
    ErrorAlert,
    SocialAuth,
    OkLogo,
    PasswordInput,
    OkTextInput,
    BButton,
    BForm,
    BFormGroup,
    BCard,
    BCardTitle,
    BSpinner,
    BLink,
    BCardText,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      remember: true,
      password: '',
      userEmail: '',
      submitting: false,
      required,
      email,
      error: null,
      socialAuth: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    async submit() {
      this.error = null
      const isValid = await this.$refs.loginForm.validate()

      if (!isValid) return

      const credentials = {
        email: this.userEmail,
        password: this.password,
        oneTime: !this.remember,
      }
      this.submitting = true
      this.$store.dispatch('auth/LOGIN', credentials)
        .then(user => {
          this.$notifySuccess(this.$t('auth.welcome-back-user', [user.name]))
          const pendingRoute = StorageService.getItem('okmenu_route_after_auth', null, -1)
          this.$router.push(pendingRoute ?? { name: 'admin:home' }).catch(() => {})
        })
        .catch(err => {
          this.$notifyError(err)
          this.error = err
          this.submitting = false
        })
    },
  },
  metaInfo() {
    return {
      title: this.$t('auth.login'),
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
